/**
 * Route paths.
 *
 * Declared once so links and guards agree. A path typed by hand at a call site
 * produces a 404 that looks like a routing bug rather than a typo.
 *
 * Lives in `lib/`, not `app/`: features need these paths to build links, and a
 * feature must not import from the app shell (plan §3.1).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.8
 */
import type { Role } from '@nexus/contracts';

export const ROUTES = {
  root: '/',
  login: '/login',
  dashboard: '/dashboard',
  startups: '/startups',
  startupNew: '/startups/new',
  startupDetail: '/startups/:id',
  pilots: '/pilots',
  pilotTimeline: '/pilots/timeline',
  pilotDetail: '/pilots/:id',
  matchmaker: '/matchmaker',
  customers: '/customers',
  customerDetail: '/customers/:id',
  assets: '/assets',
  assetMap: '/assets/map',
  assetDetail: '/assets/:id',
  contacts: '/contacts',
  contactDetail: '/contacts/:id',
  search: '/search',
  alerts: '/alerts',
  reports: '/reports',
  reportQuarterly: '/reports/quarterly',
  admin: '/admin',
} as const;

/** Builds the path to one startup. */
export function startupPath(id: string): string {
  return `${ROUTES.startups}/${id}`;
}

/** Builds the path to one pilot. */
export function pilotPath(id: string): string {
  return `${ROUTES.pilots}/${id}`;
}

/** Builds the path to one customer. */
export function customerPath(id: string): string {
  return `${ROUTES.customers}/${id}`;
}

/** Builds the path to one contact. */
export function contactPath(id: string): string {
  return `${ROUTES.contacts}/${id}`;
}

/** Builds the path to one asset. */
export function assetPath(id: string): string {
  return `${ROUTES.assets}/${id}`;
}

/**
 * The minimum role each area requires.
 *
 * A convenience for the interface only — PRD §6.2 puts the real check on the
 * server. Listing it here keeps the navigation honest about what a user can
 * actually reach.
 */
export const ROUTE_ROLES: Record<string, Role> = {
  [ROUTES.dashboard]: 'VIEWER',
  [ROUTES.startups]: 'VIEWER',
  [ROUTES.pilots]: 'VIEWER',
  [ROUTES.pilotTimeline]: 'VIEWER',
  /* Matchmaking proposes actions a viewer cannot take, so the destination is
     hidden from them rather than shown and then refused. */
  [ROUTES.matchmaker]: 'EDITOR',
  [ROUTES.customers]: 'VIEWER',
  [ROUTES.assets]: 'VIEWER',
  [ROUTES.assetMap]: 'VIEWER',
  [ROUTES.contacts]: 'VIEWER',
  [ROUTES.search]: 'VIEWER',
  [ROUTES.alerts]: 'VIEWER',
  [ROUTES.reports]: 'VIEWER',
  [ROUTES.reportQuarterly]: 'VIEWER',
  [ROUTES.admin]: 'ADMIN',
};
